import ReactMarkdown from "react-markdown";
import { useNavigate } from "react-router-dom";
import remarkGfm from "remark-gfm";
import { cn } from "@/lib/utils";

function normalizeEscapedWhitespace(text: string | null | undefined): string {
  return text?.replace(/\\r\\n/g, "\n").replace(/\\r/g, "\n").replace(/\\n/g, "\n").replace(/\\t/g, "\t") ?? "";
}

interface MarkdownRendererProps {
  content: string | null | undefined;
  className?: string;
  /** When true, external links render as plain text; internal app links (starting with /) remain clickable */
  disableLinks?: boolean | undefined;
  /**
   * When given, only links it accepts render, as in-app links that navigate
   * within the SPA; every other link renders as plain text. Takes precedence
   * over `disableLinks`. The Toolbox Help panel passes `isHelpLinkPath`
   * (epic PI-SPEC-SW-E302, feature PI-SPEC-SW-1557).
   */
  allowLink?: ((href: string) => boolean) | undefined;
}

export function MarkdownRenderer({ content, className, disableLinks, allowLink }: MarkdownRendererProps) {
  const navigate = useNavigate();
  // Help answers carry Windows paths and code, where a literal \n or \t is real text.
  const normalizedContent = allowLink ? content : normalizeEscapedWhitespace(content);

  return (
    <div className={cn("markdown-content", className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Headings
          h1: ({ children }) => <h1 className="text-2xl font-bold mt-6 mb-4 first:mt-0">{children}</h1>,
          h2: ({ children }) => <h2 className="text-xl font-bold mt-5 mb-3 first:mt-0">{children}</h2>,
          h3: ({ children }) => <h3 className="text-lg font-semibold mt-4 mb-2 first:mt-0">{children}</h3>,
          // Paragraphs
          p: ({ children }) => <p className="mb-3 last:mb-0 leading-relaxed">{children}</p>,
          // Lists
          ul: ({ children }) => <ul className="list-disc list-inside mb-3 space-y-1">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal list-inside mb-3 space-y-1">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          // Code blocks
          pre: ({ children }) => (
            <pre className="bg-muted/50 rounded-md p-3 overflow-x-auto text-sm mb-3 font-mono">
              {children}
            </pre>
          ),
          code: ({ children, className: codeClassName }) => {
            const isInline = !codeClassName;
            return isInline ? (
              <code className="bg-muted/50 rounded px-1.5 py-0.5 text-sm font-mono">{children}</code>
            ) : (
              <code className={codeClassName}>{children}</code>
            );
          },
          // Blockquote
          blockquote: ({ children }) => (
            <blockquote className="border-l-4 border-muted-foreground/30 pl-4 italic my-3">
              {children}
            </blockquote>
          ),
          // Tables
          table: ({ children }) => (
            <div className="overflow-x-auto mb-3">
              <table className="min-w-full border-collapse">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-border px-3 py-2 bg-muted text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-border px-3 py-2">{children}</td>
          ),
          // Horizontal rule
          hr: () => <hr className="my-4 border-border" />,
          // Checkboxes in task lists
          input: ({ type, checked, ...props }) => {
            if (type === "checkbox") {
              return (
                <input
                  type="checkbox"
                  checked={checked}
                  readOnly
                  className="mr-2 h-4 w-4 rounded border-gray-300"
                  {...props}
                />
              );
            }
            return <input type={type} {...props} />;
          },
          // Links — internal app links stay clickable, external links become plain text
          a: ({ href, children }) => {
            if (allowLink) {
              if (href && allowLink(href)) {
                return (
                  <a
                    href={href}
                    onClick={(e) => {
                      e.preventDefault();
                      void navigate(href);
                    }}
                    className="text-[#23A4FF] hover:text-[#6CC4FF] cursor-pointer"
                  >
                    {children}
                  </a>
                );
              }
              return <span>{children}</span>;
            }
            if (disableLinks) {
              // Internal app links — navigate within the SPA
              if (href?.startsWith("/")) {
                return (
                  <a
                    href={href}
                    onClick={(e) => {
                      e.preventDefault();
                      void navigate(href);
                    }}
                    className="text-primary underline hover:no-underline cursor-pointer"
                  >
                    {children}
                  </a>
                );
              }
              // External links — render as plain text
              return <span>{children}</span>;
            }
            // Normal mode — all links open in new tab
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline hover:no-underline"
              >
                {children}
              </a>
            );
          },
          // Images: with an allowLink policy (Toolbox Help) nothing is loaded from elsewhere; the alt text shows instead
          ...(allowLink ? { img: ({ alt }: { alt?: string | undefined }) => <span>{alt ?? ""}</span> } : {}),
          // Strong/bold
          strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
          // Emphasis/italic
          em: ({ children }) => <em className="italic">{children}</em>,
        }}
      >
        {normalizedContent}
      </ReactMarkdown>
    </div>
  );
}
